import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { get, post } from '../../helpers/apiHelper';
import publicPageCustomizationService from '../../helpers/publicPageCustomizationHelper';
import Swal from 'sweetalert2';
import QRRegisterPageContent from '../../components/public/QRRegisterPageContent';

const QRRegisterPage = () => {
    const { tiendaId } = useParams();
    const [tienda, setTienda] = useState(null);

    // Estados para personalización
    const [customization, setCustomization] = useState(null);
    const [imagenFondo, setImagenFondo] = useState(null);
    const [texts, setTexts] = useState({
        benefitsTitle: '¡Únete a nuestro club de clientes!',
        benefitsDescription: 'Disfruta de una experiencia personalizada y beneficios exclusivos:',
        benefit1Title: 'Beneficios Exclusivos',
        benefit1Description: 'Accede a ventajas especiales diseñadas para nuestros miembros',
        benefit2Title: 'Ofertas Personalizadas',
        benefit2Description: 'Recibe promociones adaptadas a tus preferencias',
        benefit3Title: 'Experiencias Únicas',
        benefit3Description: 'Sorpresas y eventos especiales para miembros',
        formTitle: 'Completa tu registro',
        personalInfoTitle: 'Información Personal',
        referralCodeTitle: '¿Tienes un código de referido?',
        referralCodePlaceholder: 'Ingresa tu código de referido (opcional)',
        geographicInfoTitle: 'Información Geográfica (Opcional)',
        submitButtonText: 'Unirme al Club de Clientes',
        termsText: 'Al registrarte, aceptas nuestros términos y condiciones y nuestra política de privacidad'
    });
    const [styles, setStyles] = useState({});
    const [layout, setLayout] = useState('classic');
    const [layoutOptions, setLayoutOptions] = useState({
        showLogo: true,
        showSlogan: false,
        logoPosition: 'center',
        headerStyle: 'bordered',
        showBenefits: true,
        benefitsLayout: 'grid'
    });

    // Función para procesar variables en textos
    const processVariables = (text, variables) => {
        if (!text) return '';
        let processed = text;
        Object.keys(variables).forEach(key => {
            const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
            processed = processed.replace(regex, variables[key] || '');
        });
        return processed;
    };

    useEffect(() => {
        async function fetchData() {
            try {
                const tiendaData = await get(`tiendas/public/${tiendaId}`);
                setTienda(tiendaData);

                // Cargar personalización usando el marcaId de la tienda
                if (tiendaData.marca) {
                    const customizationData = await publicPageCustomizationService.getCustomization(
                        tiendaData.marca,
                        'qrRegister'
                    ).catch(() => null);

                    // Procesar personalización
                    let processedCustomization = null;
                    if (customizationData) {
                        // Manejar objetos Mongoose que pueden tener _doc
                        processedCustomization = customizationData._doc || customizationData;
                    }

                    // Cargar tipografía desde personalización (solo si es una fuente de Google Fonts)
                    const fontFamilyToLoad = processedCustomization?.styles?.fontFamily;
                    if (fontFamilyToLoad) {
                        // Fuentes del sistema que NO deben cargarse desde Google Fonts
                        const systemFonts = ['Arial', 'Helvetica', 'Verdana', 'Tahoma', 'Georgia', 'Times New Roman', 'sans-serif', 'serif', 'monospace'];
                        const fontName = fontFamilyToLoad.split(',')[0].trim().replace(/['"]/g, '');

                        if (!systemFonts.includes(fontName)) {
                            const link = document.createElement('link');
                            link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(' ', '+')}&display=swap`;
                            link.rel = 'stylesheet';
                            document.head.appendChild(link);
                        }
                    }

                    // Procesar personalización
                    if (processedCustomization && processedCustomization.isActive !== false) {
                        setCustomization(processedCustomization);

                        // Aplicar textos personalizados (con procesamiento de variables)
                        const variables = {
                            tiendaNombre: tiendaData.nombre || '',
                            tiendaDescripcion: tiendaData.descripcion || '',
                            tiendaDireccion: `${tiendaData.via} ${tiendaData.calle}, ${tiendaData.numero}, ${tiendaData.poblacion}, ${tiendaData.pais}`,
                            brandName: tiendaData.marcaNombre || ''
                        };

                        const processedTexts = {};
                        const textsToProcess = processedCustomization.texts || {};
                        Object.keys(textsToProcess).forEach(key => {
                            processedTexts[key] = processVariables(textsToProcess[key], variables);
                        });

                        setTexts(prev => ({ ...prev, ...processedTexts }));

                        // Aplicar estilos personalizados
                        if (processedCustomization.styles) {
                            setStyles(processedCustomization.styles);
                        }

                        // Aplicar layout y opciones
                        if (processedCustomization.layout) {
                            setLayout(processedCustomization.layout);
                        }
                        if (processedCustomization.layoutOptions) {
                            // Filtrar solo las opciones válidas para qrRegister
                            const validOptions = {
                                showLogo: processedCustomization.layoutOptions.showLogo,
                                showSlogan: processedCustomization.layoutOptions.showSlogan,
                                logoPosition: processedCustomization.layoutOptions.logoPosition,
                                headerStyle: processedCustomization.layoutOptions.headerStyle,
                                benefitsLayout: processedCustomization.layoutOptions.benefitsLayout,
                                showBenefits: processedCustomization.layoutOptions.showBenefits,
                                showBrandName: processedCustomization.layoutOptions.showBrandName,
                                showOverlay: processedCustomization.layoutOptions.showOverlay,
                                showLocation: processedCustomization.layoutOptions.showLocation
                            };
                            // Eliminar valores undefined
                            Object.keys(validOptions).forEach(key => {
                                if (validOptions[key] === undefined) {
                                    delete validOptions[key];
                                }
                            });
                            setLayoutOptions(prev => ({ ...prev, ...validOptions }));
                        }

                        // Aplicar imagen de fondo si existe
                        if (processedCustomization.imagenFondo) {
                            setImagenFondo(processedCustomization.imagenFondo);
                        }
                    } else {
                        // Usar valores por defecto, pero procesar variables en textos por defecto
                        const variables = {
                            tiendaNombre: tiendaData.nombre || '',
                            tiendaDescripcion: tiendaData.descripcion || '',
                            tiendaDireccion: `${tiendaData.via} ${tiendaData.calle}, ${tiendaData.numero}, ${tiendaData.poblacion}, ${tiendaData.pais}`,
                            brandName: tiendaData.marcaNombre || ''
                        };
                        const defaultTexts = { ...texts };
                        Object.keys(defaultTexts).forEach(key => {
                            defaultTexts[key] = processVariables(defaultTexts[key], variables);
                        });
                        setTexts(defaultTexts);
                    }
                }
            } catch (error) {
                // Error silencioso - la página se renderiza con valores por defecto
            }
        }
        fetchData();
    }, [tiendaId]);

    const handleSubmit = async (formData, codigoReferido) => {
        const telefonoCompleto = formData.telefonoPrefix + formData.telefonoNumber;
        const { telefonoPrefix, telefonoNumber, ...rest } = formData;
        const payload = {
            ...rest,
            telefono: telefonoCompleto,
            geographic_info: { ...rest.geographic_info, postal_code: '' },
            marca: tienda.marca,
            tienda: tienda._id,
        };

        if (codigoReferido.trim() !== '') {
            const cleanedCode = codigoReferido.trim().toLowerCase().replace(/\s+/g, '');
            try {
                const refResponse = await get(
                    `clientes/referral?code=${cleanedCode}&marcaId=${tienda.marca}`
                );
                if (!refResponse || !refResponse._id) {
                    Swal.fire({
                        title: 'Error',
                        text: 'El código de referido no es válido o no pertenece a un cliente de tu marca',
                        icon: 'error',
                        confirmButtonText: 'Aceptar'
                    });
                    return;
                }
                payload.referidoPor = refResponse._id;
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: 'Error al validar el código de referido',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
                return;
            }
        }

        try {
            await post('clientes/qr-register', payload);
            const successMessage = customization?.texts?.successMessage || '¡Te has registrado correctamente!';
            Swal.fire('Registro Exitoso', successMessage, 'success');
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    if (!tienda) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-pulse text-gray-600 flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-t-blue-500 border-gray-200 rounded-full animate-spin mb-4"></div>
                    <p className="text-lg">Cargando información de la tienda...</p>
                </div>
            </div>
        );
    }

    return (
        <QRRegisterPageContent
            tienda={tienda}
            texts={texts}
            styles={styles}
            layout={layout}
            layoutOptions={layoutOptions}
            imagenFondo={imagenFondo}
            onSubmit={handleSubmit}
            isPreview={false}
        />
    );
};

export default QRRegisterPage;
